import React from 'react';
import { Link } from 'react-router-dom';

import {
  UniActionButton, UniKpiCard, UniPageShell, UniSection, UniTable, useUniList,
} from '../waba/WabaShared.jsx';
import {
  HOME, SB_API, THEME, formatInTimeZone, useDisplayTimezone,
} from './SignalBridgeShared.jsx';

const P = { theme: THEME, api: SB_API, home: HOME };

function localISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function daysAgo(n) {
  const date = new Date();
  date.setDate(date.getDate() - n);
  return localISO(date);
}

export function formatMoney(amount, currency) {
  const n = Number(amount);
  const text = Number.isFinite(n)
    ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : String(amount ?? '0');
  if (!currency || currency === 'INR') return `₹${text}`;
  return `${currency} ${text}`;
}

function paymentLabel(value) {
  if (value === 'PREPAID') return 'Prepaid';
  if (value === 'COD') return 'COD';
  if (!value || value === 'NA') return '—';
  return value;
}

export function RankCard({ title, rows, empty }) {
  const list = rows || [];
  const max = Math.max(1, ...list.map((row) => Number(row.orders) || 0));
  return (
    <section className="sb-rank">
      <h3>{title}</h3>
      {list.length === 0 ? <p className="sb-rank__empty">{empty}</p> : list.map((row) => {
        const orders = Number(row.orders) || 0;
        const width = orders ? `${(100 * orders) / max}%` : '0%';
        return (
          <div key={row.label} className="sb-rank__row">
            <div className="sb-rank__top">
              <span>{row.label}</span>
              <span className="sb-rank__meta">{orders} orders · {row.share_pct}%</span>
            </div>
            <div className="sb-rank__track" aria-hidden="true">
              <span className="sb-rank__bar" style={{ width }} />
            </div>
          </div>
        );
      })}
    </section>
  );
}

function MixLine({ buyer }) {
  const currency = buyer.currency || 'INR';
  const payment = (buyer.payment || [])
    .filter((row) => row.orders)
    .map((row) => `${row.label} ${row.orders} · ${formatMoney(row.revenue, currency)}`);
  const traffic = (buyer.traffic || [])
    .filter((row) => row.orders)
    .map((row) => `${row.label} ${row.share_pct}%`);
  if (!payment.length && !traffic.length) return null;
  return (
    <p className="sb-insight-lead">
      {payment.join('  ·  ')}
      {payment.length && traffic.length ? '  ·  ' : ''}
      {traffic.join('  ·  ')}
    </p>
  );
}

export function BuyerSummary({ buyer, loading, error }) {
  const data = buyer || {};
  const currency = data.currency || 'INR';
  const range = data.since && data.until ? `${data.since} to ${data.until}` : 'last 7 days';
  return (
    <>
      <UniSection title="Who is buying" />
      <p className="sb-insight-lead">
        Purchases {range}. Entry point (Facebook, Instagram, Google, and others), device, and state.
        {' '}
        <Link to="/signalbridge/purchase-report">Open purchase report</Link>
      </p>
      {loading ? <div className="uni-state">Loading…</div> : error ? (
        <div className="ty-error">{String(error.message || error)}</div>
      ) : !data.orders ? (
        <p className="sb-rank__empty">
          No purchases in this range yet. Entry point, device, and state show here after the first order.
        </p>
      ) : (
        <>
          <div className="uni-kpis">
            <UniKpiCard icon="◎" label="Orders" value={String(data.orders)} to="/signalbridge/purchase-report" />
            <UniKpiCard icon="₹" label="Revenue" value={formatMoney(data.revenue, currency)} to="/signalbridge/purchase-report" />
            <UniKpiCard icon="₹" label="Average order" value={formatMoney(data.aov, currency)} />
            <UniKpiCard icon="↻" label="Repeat buyers" value={`${data.repeat_buyers || 0} · ${data.repeat_buyer_pct || 0}%`} />
            <UniKpiCard icon="↗" label="Known source" value={`${data.known_source_pct || 0}%`} />
            <UniKpiCard icon="▣" label="Paid traffic" value={`${data.paid_pct || 0}%`} />
          </div>
          <div className="sb-insights">
            <RankCard title="Top entry points" rows={data.entry_points} empty="No source on these orders" />
            <RankCard title="Devices" rows={data.devices} empty="No device on these orders" />
            <RankCard title="States" rows={data.states} empty="No state on these orders" />
            <RankCard title="Cities" rows={data.cities} empty="No city on these orders" />
            <RankCard title="Campaigns" rows={data.campaigns} empty="No campaign on these orders" />
            <RankCard title="Browsers" rows={data.browsers} empty="No browser on these orders" />
          </div>
          <MixLine buyer={data} />
        </>
      )}
    </>
  );
}

function CountTable({ title, rows, currency, units }) {
  const columns = [
    { key: 'label', label: 'Name' },
    { key: 'orders', label: 'Orders', align: 'right' },
  ];
  if (units) {
    columns.push({ key: 'units', label: 'Units', align: 'right' });
  } else {
    columns.push({
      key: 'revenue',
      label: 'Revenue',
      align: 'right',
      render: (row) => (row.revenue == null || row.revenue === '' ? '—' : formatMoney(row.revenue, currency)),
    });
    columns.push({
      key: 'aov',
      label: 'AOV',
      align: 'right',
      render: (row) => (row.aov == null || row.aov === '' ? '—' : formatMoney(row.aov, currency)),
    });
  }
  columns.push({ key: 'share_pct', label: 'Share %', align: 'right' });
  return (
    <>
      <UniSection title={title} />
      <UniTable rows={rows || []} empty="Nothing in this range" rowKey="label" columns={columns} />
    </>
  );
}

export function BuyerReportBody({ data }) {
  const buyer = data || {};
  const currency = buyer.currency || 'INR';
  const tz = useDisplayTimezone();
  return (
    <>
      <p className="sb-insight-lead">
        {buyer.orders || 0} purchases
        {buyer.since ? ` from ${buyer.since} to ${buyer.until}` : ''}.
        {' '}
        {buyer.note}
      </p>
      <div className="uni-kpis">
        <UniKpiCard icon="◎" label="Orders" value={String(buyer.orders || 0)} />
        <UniKpiCard icon="₹" label="Revenue" value={formatMoney(buyer.revenue || 0, currency)} />
        <UniKpiCard icon="₹" label="Average order" value={formatMoney(buyer.aov || 0, currency)} />
        <UniKpiCard icon="↻" label="Repeat buyers" value={`${buyer.repeat_buyers || 0} of ${buyer.buyers || 0}`} />
        <UniKpiCard icon="↗" label="Known source" value={`${buyer.known_source_pct || 0}%`} />
        <UniKpiCard icon="▣" label="Paid traffic" value={`${buyer.paid_pct || 0}%`} />
      </div>
      <div className="sb-insights">
        <RankCard title="Entry points" rows={(buyer.entry_points || []).slice(0, 5)} empty="No source on these orders" />
        <RankCard title="Devices" rows={buyer.devices} empty="No device on these orders" />
        <RankCard title="States" rows={(buyer.states || []).slice(0, 5)} empty="No state on these orders" />
      </div>
      <CountTable title="Entry points" rows={buyer.entry_points} currency={currency} />
      <CountTable title="Devices" rows={buyer.devices} currency={currency} />
      <CountTable title="How they arrived" rows={buyer.traffic} currency={currency} />
      <CountTable title="Payment" rows={buyer.payment} currency={currency} />
      <CountTable title="Browsers" rows={buyer.browsers} currency={currency} />
      <CountTable title="States" rows={buyer.states} currency={currency} />
      <CountTable title="Cities" rows={buyer.cities} currency={currency} />
      <CountTable title="Countries" rows={buyer.countries} currency={currency} />
      <CountTable title="Campaigns" rows={buyer.campaigns} currency={currency} />
      <CountTable title="Products" rows={buyer.products} currency={currency} units />
      <CountTable title="Weekday" rows={buyer.weekdays} currency={currency} />
      {(buyer.checkout || []).length > 0 && (
        <CountTable title="Checkout source" rows={buyer.checkout} currency={currency} />
      )}
      <UniSection title="Orders" />
      <UniTable
        rows={buyer.orders_rows || []}
        empty="No purchases in this range"
        rowKey={(row, index) => row.id || row.event_id || index}
        columns={[
          {
            key: 'order_number',
            label: 'Order',
            render: (row) => (
              row.id
                ? <Link to={`/signalbridge/events/${row.id}`}>{row.order_number || row.event_id}</Link>
                : (row.order_number || row.event_id)
            ),
          },
          {
            key: 'when',
            label: 'When',
            render: (row) => (row.when ? formatInTimeZone(row.when, tz) : '—'),
          },
          { key: 'entry_point', label: 'Entry' },
          { key: 'device', label: 'Device' },
          { key: 'browser', label: 'Browser' },
          { key: 'state', label: 'State' },
          { key: 'city', label: 'City' },
          { key: 'utm_campaign', label: 'Campaign', render: (row) => row.utm_campaign || '—' },
          { key: 'payment_type', label: 'Payment', render: (row) => paymentLabel(row.payment_type) },
          {
            key: 'value',
            label: 'Value',
            align: 'right',
            render: (row) => formatMoney(row.value, row.currency || currency),
          },
        ]}
      />
    </>
  );
}

export function SignalBridgeBuyerReport() {
  const [since, setSince] = React.useState(() => daysAgo(29));
  const [until, setUntil] = React.useState(() => daysAgo(0));
  const qs = new URLSearchParams({ type: 'BUYER' });
  if (since) qs.set('since', since);
  if (until) qs.set('until', until);
  const { raw, loading, error, reload } = useUniList(`${SB_API}/reports/?${qs}`, [since, until]);
  const exportFmt = (fmt) => {
    window.location.href = `${SB_API}/reports/?${qs}&format=${fmt}`;
  };
  const applyRange = (days) => {
    setSince(daysAgo(days - 1));
    setUntil(daysAgo(0));
  };
  return (
    <UniPageShell
      {...P}
      icon="▤"
      title="Purchase report"
      helpPage="buyer_report"
      helpApi={SB_API}
      subtitle="Where buyers came from, which device they used, and which state the order is from."
      actions={(
        <>
          <UniActionButton hero onClick={() => exportFmt('csv')}>CSV</UniActionButton>
          <UniActionButton hero onClick={() => exportFmt('xlsx')}>Excel</UniActionButton>
          <UniActionButton hero to={HOME}>← Home</UniActionButton>
        </>
      )}
    >
      <div className="uni-toolbar sb-insight-filters">
        <button type="button" className="ty-btn ty-btn--secondary" onClick={() => applyRange(7)}>7 days</button>
        <button type="button" className="ty-btn ty-btn--secondary" onClick={() => applyRange(30)}>30 days</button>
        <button type="button" className="ty-btn ty-btn--secondary" onClick={() => applyRange(90)}>90 days</button>
        <input type="date" value={since} onChange={(e) => setSince(e.target.value)} aria-label="From" />
        <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} aria-label="To" />
        <UniActionButton hero onClick={() => reload && reload()}>Refresh</UniActionButton>
      </div>
      {loading && <div className="uni-state">Loading…</div>}
      {error && <div className="ty-error">{String(error.message || error)}</div>}
      {!loading && !error && <BuyerReportBody data={raw} />}
    </UniPageShell>
  );
}
